import type { ReactElement } from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTrigger,
  Button,
} from '@/components';
import { useOptOutMutation } from '@/features/account';
import { logEvent, useOptOutStatus } from '@/features/analytics';
import { optOutTracking } from '@/utils/opt-out';

import {
  AccountSection,
  AccountSectionDescription,
  AccountSectionFooter,
  AccountSectionFooterActions,
  AccountSectionHeader,
  AccountSectionHeaderContainer,
  AccountSectionSpacer,
  AccountSectionTitle,
} from '../../../components/AccountSection/AccountSection';
import { OptOutContent } from './PopupContent/OptOutContent';

export function OptOut(): ReactElement {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [optOut, { isLoading }] = useOptOutMutation();
  const [optOutStatus, setOptOutStatus] = useOptOutStatus();

  const handleOptOut = async (): Promise<void> => {
    try {
      await optOut(undefined).unwrap();
      void setOptOutStatus('confirmed');
      optOutTracking();
    } catch (error) {
      toast.error('Failed to send opt-out request. Please try again.');
    }
  };

  const optOutStatusText = {
    pending: 'Pending',
    confirmed: 'Confirmed',
  };

  return (
    <AccountSection>
      <AccountSectionHeader>
        <AccountSectionHeaderContainer>
          <AccountSectionTitle>Data Sharing Opt-Out</AccountSectionTitle>
          <AccountSectionDescription>
            Request to stop sharing your data for marketing and ad analytics
            purposes.
          </AccountSectionDescription>
        </AccountSectionHeaderContainer>
      </AccountSectionHeader>

      <AccountSectionSpacer />

      <AccountSectionFooter
        variant={optOutStatus === 'pending' ? 'normal' : 'destructive'}
      >
        <AccountSectionFooterActions>
          <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="small"
                disabled={
                  optOutStatus === 'pending' || optOutStatus === 'confirmed'
                }
                onClick={() => {
                  logEvent('btn_opt_out');
                }}
              >
                {optOutStatus === null
                  ? 'Submit Request'
                  : optOutStatusText[optOutStatus]}
              </Button>
            </AlertDialogTrigger>

            <AlertDialogContent>
              <OptOutContent
                status={optOutStatus}
                isLoading={isLoading}
                onClick={() => {
                  if (
                    optOutStatus !== 'pending' &&
                    optOutStatus !== 'confirmed'
                  ) {
                    void handleOptOut();
                  } else {
                    setIsDialogOpen(false);
                  }
                }}
              />
            </AlertDialogContent>
          </AlertDialog>
        </AccountSectionFooterActions>
      </AccountSectionFooter>
    </AccountSection>
  );
}
